import { useState } from 'react';
import { toast } from 'sonner';
import { useAuditLogs, useErrorLogs, useLogsStats, useEntityTypes } from '../../api/adminLogs';
import { useExportAuditLogs } from '../../api/admin';
import { useUserList } from '../../api/users';
import type {
  QueryAuditLogsParams,
  QueryErrorLogsParams,
  AuditAction,
  ErrorSeverity,
  AuditLog,
  ErrorLog,
} from '../../types/adminLogs';

type TabType = 'audit' | 'errors';

const actionLabels: Record<AuditAction, string> = {
  CREATE: 'Erstellt',
  UPDATE: 'Aktualisiert',
  DELETE: 'Gelöscht',
  LOGIN: 'Anmeldung',
  LOGOUT: 'Abmeldung',
};

const severityConfig: Record<ErrorSeverity, { label: string; color: string; bgColor: string }> = {
  LOW: { label: 'Niedrig', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  MEDIUM: { label: 'Mittel', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  HIGH: { label: 'Hoch', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  CRITICAL: { label: 'Kritisch', color: 'text-red-700', bgColor: 'bg-red-100' },
};

export function AdminLogsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('audit');
  const [auditQuery, setAuditQuery] = useState<QueryAuditLogsParams>({ page: 1, limit: 20 });
  const [errorQuery, setErrorQuery] = useState<QueryErrorLogsParams>({ page: 1, limit: 20 });
  const [auditSearch, setAuditSearch] = useState('');
  const [errorSearch, setErrorSearch] = useState('');
  const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);

  const { data: statsData } = useLogsStats();
  const { data: entityTypesData } = useEntityTypes();
  const { data: usersData } = useUserList({ limit: 100 });
  const { data: auditData, isLoading: auditLoading } = useAuditLogs(auditQuery);
  const { data: errorData, isLoading: errorLoading } = useErrorLogs(errorQuery);
  const exportMutation = useExportAuditLogs();

  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync({
        action: auditQuery.action,
        entityType: auditQuery.entityType,
      });
      toast.success('Export gestartet');
    } catch (err) {
      toast.error('Fehler beim Export');
    }
  };

  const stats = statsData?.data;
  const entityTypes = entityTypesData?.data || [];
  const users = [...(usersData?.data || [])].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'de')
  );

  const handleAuditSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAuditQuery((prev) => ({ ...prev, search: auditSearch || undefined, page: 1 }));
  };

  const handleErrorSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorQuery((prev) => ({ ...prev, search: errorSearch || undefined, page: 1 }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderAuditLogRow = (log: AuditLog) => {
    const isExpanded = expandedAuditId === log.id;
    return (
      <tr
        key={log.id}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpandedAuditId(isExpanded ? null : log.id)}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(log.timestamp)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {log.user ? `${log.user.firstName} ${log.user.lastName}` : '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
            log.action === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
            log.action === 'DELETE' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {actionLabels[log.action]}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {log.entityType}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
          {log.entityId?.substring(0, 8)}...
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {log.ipAddress || '-'}
        </td>
      </tr>
    );
  };

  const renderAuditLogDetails = (log: AuditLog) => {
    if (expandedAuditId !== log.id) return null;
    return (
      <tr key={`${log.id}-details`}>
        <td colSpan={6} className="px-6 py-4 bg-gray-50">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Entity ID</h4>
                <p className="text-sm text-gray-900 font-mono">{log.entityId}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">User Agent</h4>
                <p className="text-sm text-gray-900 truncate" title={log.userAgent || '-'}>
                  {log.userAgent || '-'}
                </p>
              </div>
            </div>
            {log.changes && Object.keys(log.changes).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Änderungen</h4>
                <pre className="text-sm bg-gray-100 p-3 rounded-md overflow-auto max-h-64">
                  {JSON.stringify(log.changes, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderErrorLogRow = (log: ErrorLog) => {
    const isExpanded = expandedErrorId === log.id;
    const severity = severityConfig[log.severity];
    return (
      <tr
        key={log.id}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpandedErrorId(isExpanded ? null : log.id)}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(log.timestamp)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severity.bgColor} ${severity.color}`}>
            {severity.label}
          </span>
        </td>
        <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate" title={log.message}>
          {log.message}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {log.path || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {log.statusCode || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {log.user ? `${log.user.firstName} ${log.user.lastName}` : '-'}
        </td>
      </tr>
    );
  };

  const renderErrorLogDetails = (log: ErrorLog) => {
    if (expandedErrorId !== log.id) return null;
    return (
      <tr key={`${log.id}-details`}>
        <td colSpan={6} className="px-6 py-4 bg-gray-50">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Context</h4>
                <p className="text-sm text-gray-900">{log.context || '-'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Method</h4>
                <p className="text-sm text-gray-900">{log.method || '-'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">IP Address</h4>
                <p className="text-sm text-gray-900">{log.ipAddress || '-'}</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Full Message</h4>
              <p className="text-sm text-gray-900">{log.message}</p>
            </div>
            {log.stack && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Stack Trace</h4>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-md overflow-auto max-h-64">
                  {log.stack}
                </pre>
              </div>
            )}
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Metadata</h4>
                <pre className="text-sm bg-gray-100 p-3 rounded-md overflow-auto max-h-48">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderPagination = (
    meta: { page: number; limit: number; total: number; totalPages: number } | undefined,
    onPageChange: (page: number) => void
  ) => {
    if (!meta || meta.totalPages <= 1) return null;
    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Zeige <span className="font-medium">{(meta.page - 1) * meta.limit + 1}</span>
              {' '}-{' '}
              <span className="font-medium">{Math.min(meta.page * meta.limit, meta.total)}</span>
              {' '}von <span className="font-medium">{meta.total}</span> Einträgen
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => onPageChange(meta.page - 1)}
                disabled={meta.page <= 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                Seite {meta.page} von {meta.totalPages}
              </span>
              <button
                onClick={() => onPageChange(meta.page + 1)}
                disabled={meta.page >= meta.totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System-Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Aktivitäts- und Fehlerprotokolle zur Systemüberwachung
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {exportMutation.isPending ? 'Exportiert...' : 'Audit-Logs exportieren'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Audit-Logs (24h)</dt>
                    <dd className="text-lg font-semibold text-gray-900">{stats.auditLogsLast24h}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Fehler (24h)</dt>
                    <dd className="text-lg font-semibold text-gray-900">{stats.errorLogsLast24h}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Gesamt Audit-Logs</dt>
                    <dd className="text-lg font-semibold text-gray-900">{stats.auditLogsCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-100 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Fehler (7 Tage)</dt>
                    <dd className="text-lg font-semibold text-gray-900">{stats.recentErrorsCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Aktivitäten
            {auditData?.meta && (
              <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
                {auditData.meta.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'errors'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fehler
            {errorData?.meta && (
              <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                errorData.meta.total > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-900'
              }`}>
                {errorData.meta.total}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Audit Tab Content */}
      {activeTab === 'audit' && (
        <>
          {/* Filters */}
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
                <form onSubmit={handleAuditSearch} className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suche</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      placeholder="Entity Type, Entity ID..."
                      className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                </form>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aktion</label>
                  <select
                    value={auditQuery.action || ''}
                    onChange={(e) => setAuditQuery((prev) => ({ ...prev, action: (e.target.value as AuditAction) || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle Aktionen</option>
                    <option value="CREATE">Erstellt</option>
                    <option value="UPDATE">Aktualisiert</option>
                    <option value="DELETE">Gelöscht</option>
                    <option value="LOGIN">Anmeldung</option>
                    <option value="LOGOUT">Abmeldung</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                  <select
                    value={auditQuery.entityType || ''}
                    onChange={(e) => setAuditQuery((prev) => ({ ...prev, entityType: e.target.value || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle Typen</option>
                    {entityTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benutzer</label>
                  <select
                    value={auditQuery.userId || ''}
                    onChange={(e) => setAuditQuery((prev) => ({ ...prev, userId: e.target.value || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle Benutzer</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {auditLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Laden...</p>
              </div>
            ) : auditData?.data.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Audit-Logs gefunden</h3>
                <p className="mt-1 text-sm text-gray-500">Ändern Sie Ihre Suchkriterien.</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zeitpunkt</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktion</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP-Adresse</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditData?.data.flatMap((log) => [renderAuditLogRow(log), renderAuditLogDetails(log)])}
                  </tbody>
                </table>
                {renderPagination(auditData?.meta, (page) => setAuditQuery((prev) => ({ ...prev, page })))}
              </>
            )}
          </div>
        </>
      )}

      {/* Errors Tab Content */}
      {activeTab === 'errors' && (
        <>
          {/* Filters */}
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
                <form onSubmit={handleErrorSearch} className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suche</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={errorSearch}
                      onChange={(e) => setErrorSearch(e.target.value)}
                      placeholder="Nachricht, Pfad..."
                      className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                </form>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schweregrad</label>
                  <select
                    value={errorQuery.severity || ''}
                    onChange={(e) => setErrorQuery((prev) => ({ ...prev, severity: (e.target.value as ErrorSeverity) || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle</option>
                    <option value="LOW">Niedrig</option>
                    <option value="MEDIUM">Mittel</option>
                    <option value="HIGH">Hoch</option>
                    <option value="CRITICAL">Kritisch</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                  <select
                    value={errorQuery.level || ''}
                    onChange={(e) => setErrorQuery((prev) => ({ ...prev, level: (e.target.value as 'error' | 'warn' | 'fatal') || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle</option>
                    <option value="warn">Warning</option>
                    <option value="error">Error</option>
                    <option value="fatal">Fatal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benutzer</label>
                  <select
                    value={errorQuery.userId || ''}
                    onChange={(e) => setErrorQuery((prev) => ({ ...prev, userId: e.target.value || undefined, page: 1 }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Alle Benutzer</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {errorLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Laden...</p>
              </div>
            ) : errorData?.data.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Fehler gefunden</h3>
                <p className="mt-1 text-sm text-gray-500">Das System läuft ohne Fehler.</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zeitpunkt</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schweregrad</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nachricht</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pfad</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzer</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {errorData?.data.flatMap((log) => [renderErrorLogRow(log), renderErrorLogDetails(log)])}
                  </tbody>
                </table>
                {renderPagination(errorData?.meta, (page) => setErrorQuery((prev) => ({ ...prev, page })))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminLogsPage;
